import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from '../utils/Modal/Modal';
import styles from './ShareModal.module.scss';
import { getSharedSongUrl } from '../../utils/shareSong';
import logger from '../../utils/logger';

const COPIED_HOLD_MS = 1800;

const copyToClipboard = async (text) => {
  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function'
  ) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      logger.warn('[ShareModal] clipboard.writeText failed', err);
    }
  }

  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (err) {
    logger.error('[ShareModal] legacy copy failed', err);
    return false;
  }
};

const ShareModal = ({ show, song, onClose, onCopyResult }) => {
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);
  const copiedTimerRef = useRef(null);

  const shareUrl = song?.id ? getSharedSongUrl(song.id) : '';

  // Reset "Copied" state each time a new modal instance is shown.
  useEffect(() => {
    if (show) {
      setCopied(false);
    }
    return () => {
      if (copiedTimerRef.current) {
        clearTimeout(copiedTimerRef.current);
        copiedTimerRef.current = null;
      }
    };
  }, [show, song?.id]);

  const handleCopy = useCallback(async () => {
    const ok = await copyToClipboard(shareUrl);
    if (ok) {
      setCopied(true);
      if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = setTimeout(
        () => setCopied(false),
        COPIED_HOLD_MS
      );
    }
    if (typeof onCopyResult === 'function') {
      onCopyResult(ok ? 'copied' : 'failed');
    }
  }, [shareUrl, onCopyResult]);

  const handleInputFocus = (e) => {
    e.target.select();
  };

  return (
    <Modal show={show} onClose={onClose} size="dialog" title="Share song">
      <div className={styles.content}>
        {song && (
          <div className={styles.songContext}>
            {song.thumbnailURL && (
              <img
                src={song.thumbnailURL}
                alt=""
                className={styles.thumbnail}
                aria-hidden="true"
              />
            )}
            <div className={styles.songMeta}>
              <span className={styles.songName}>{song.songName}</span>
              {song.songArtist && (
                <span className={styles.songArtist}>by {song.songArtist}</span>
              )}
            </div>
          </div>
        )}

        <div>
          <div className={styles.linkRow}>
            <input
              ref={inputRef}
              type="text"
              readOnly
              value={shareUrl}
              className={styles.linkInput}
              onFocus={handleInputFocus}
              aria-label="Shareable song URL"
            />
            <button
              type="button"
              onClick={handleCopy}
              className={`${styles.copyButton} ${
                copied ? styles.copyButtonCopied : ''
              }`}
              aria-live="polite"
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ShareModal;
